import { classes } from "lib/actions/util";
import { useCurrentProject } from "lib/hooks/useProject";
import { useLayersCriteriaSync } from "lib/hooks/layers/useLayersCriteriaSync";
import AuthGuard from "components/auth/AuthGuard";
import { KeyframeViewer } from "lib/contexts/ProjectContexts/SelectedViewerContext";
import { useVisibleViewer } from "lib/hooks/useVisibleViewer";
import SidePanel from "components/layouts/elements/SidePanel/SidePanel";
import Collapsible from "components/layouts/elements/SidePanel/Collapsible/Collapsible";
import DrawerAnimated from "components/layouts/elements/SidePanel/Collapsible/DrawerAnimated";
import UploadDialog from "components/modules/UploadDialog/UploadDialog";
import UploadPreprocessorDialog from "components/modules/UploadPreprocessing/Dialog/UploadPreprocessorDialog";
import CesiumViewerAnchor from "../elements/CesiumViewer/CesiumViewerAnchor";
import PdfTronHost from "../elements/PdfTronViewer/PdfTronViewerHost";
import Header from "./elements/Header/Header";
import SideNav from "./elements/SideNav/SideNav";
import FileDropzoneFullScreen from "./elements/FileDropzoneFullScreen";
import PannellumViewerHost from "components/elements/PannellumViewer/PannellumViewerHost";
import MobileDeviceCheck from "components/mobile/MobileDeviceCheck";
import { useViewsMode } from "lib/contexts/SpatialContexts/ViewsModeContext";
import ViewModeBar from "components/modules/Views/ViewModeBar";
import ViewSnapshotPanel from "components/modules/Views/ViewSnapshotPanel";

export default function SpatialViewerLayout(p: React.PropsWithChildren<{}>) {
  const { isProjectOpen } = useCurrentProject();
  const { viewer, isViewSnapshotShowing } = useVisibleViewer();
  const { loadedView } = useViewsMode();

  useLayersCriteriaSync();

  let content = (
    <FileDropzoneFullScreen
      className="flex min-h-0 flex-1 items-stretch gap-4"
      frostZoneClassName="rounded-xl"
    >
      <Collapsible className="-ml-4">
        {(isExpanded) => (
          <DrawerAnimated
            open={isExpanded}
            className="flex h-full items-stretch gap-4 pl-4"
          >
            <SidePanel className="w-fit">{p.children}</SidePanel>
          </DrawerAnimated>
        )}
      </Collapsible>
      <div className="relative flex-1 overflow-hidden rounded-xl shadow-1">
        {viewer === KeyframeViewer.Cesium && (
          <CesiumViewerAnchor
            className="absolute inset-0"
            isHidden={isViewSnapshotShowing}
          />
        )}
        {isViewSnapshotShowing && <ViewSnapshotPanel />}
        {loadedView && viewer === KeyframeViewer.Cesium && <ViewModeBar />}
        <div
          className={classes(
            viewer === KeyframeViewer.PdfTron ? "visible" : "invisible",
            "absolute inset-0",
          )}
        >
          <PdfTronHost />
        </div>
        {viewer === KeyframeViewer.Pannellum && <PannellumViewerHost />}
      </div>
    </FileDropzoneFullScreen>
  );

  return (
    <AuthGuard>
      <MobileDeviceCheck>
        <div className="flex h-screen items-stretch gap-4 px-5 py-3">
          {isProjectOpen && <SideNav className="shrink-0" />}
          <div className="flex h-full flex-1 flex-col items-stretch">
            <Header />
            {content}
          </div>
        </div>
        {isProjectOpen && (
          <>
            <UploadDialog />
            <UploadPreprocessorDialog />
          </>
        )}
      </MobileDeviceCheck>
    </AuthGuard>
  );
}
